import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import es from "@/i18n/es.json";
import fr from "@/i18n/fr.json";
import zh from "@/i18n/zh.json";
import hi from "@/i18n/hi.json";
import ar from "@/i18n/ar.json";
import pt from "@/i18n/pt.json";
import bn from "@/i18n/bn.json";
import ru from "@/i18n/ru.json";

export const LANGS = [["en", "English"], ["es", "Español"], ["fr", "Français"], ["zh", "中文"], ["hi", "हिन्दी"], ["ar", "العربية"], ["pt", "Português"], ["bn", "বাংলা"], ["ru", "Русский"]];
const DICTS = { es, fr, zh, hi, ar, pt, bn, ru };
export const LANG_KEY = "mfc_lang";
export const getLang = () => {
  const l = localStorage.getItem(LANG_KEY);
  return LANGS.some(([k]) => k === l) ? l : "en";
};

const esc = (s) => s.replace(/[.*+?^$()|[\]\\]/g, "\\$&");
const build = (dict) => {
  const patterns = Object.entries(dict)
    .filter(([k]) => /\{\d\}/.test(k))
    .map(([k, v]) => ({ re: new RegExp(`^${esc(k).replace(/\\?\{(\d)\\?\}/g, "(.+?)")}$`), v }));
  return (s) => {
    if (dict[s] !== undefined) return dict[s];
    for (const p of patterns) {
      const m = s.match(p.re);
      if (m) return p.v.replace(/\{(\d)\}/g, (_, i) => m[Number(i) + 1] ?? "");
    }
    return null;
  };
};

const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const SOURCES = new WeakMap();
export const CATALOG = new Map();
export const REVIEW_KEY = "mfc_review";
export const isReview = () => localStorage.getItem(REVIEW_KEY) === "1";
const norm = (s) => s.replace(/\s+/g, " ").trim();

export const findSource = (el) => {
  for (let e = el, depth = 0; e && depth < 3; e = e.parentElement, depth++) {
    for (const n of e.childNodes) {
      if (n.nodeType === 3 && SOURCES.has(n)) return { ...SOURCES.get(n), current: norm(n.nodeValue) };
    }
  }
  return null;
};

const startDomTranslation = (lookup) => {
  const doText = (n) => {
    const v = n.nodeValue;
    const t = v && v.replace(/\s+/g, " ").trim();
    if (!t || t.length > 500) return;
    const p = n.parentElement;
    if (!p || p.closest("script,style,[data-no-translate]")) return;
    const tr = lookup(t);
    if (tr && tr !== t) {
      SOURCES.set(n, { kind: "ui", key: t, source: t });
      n.nodeValue = (/^\s/.test(v) ? " " : "") + tr + (/\s$/.test(v) ? " " : "");
    } else if (CATALOG.size && CATALOG.has(t)) {
      SOURCES.set(n, { kind: "content", ...CATALOG.get(t) });
    }
  };
  const doEl = (el) => {
    ATTRS.forEach((a) => {
      const v = el.getAttribute && el.getAttribute(a);
      if (v) { const tr = lookup(v.trim()); if (tr && tr !== v) el.setAttribute(a, tr); }
    });
  };
  const walk = (root) => {
    if (root.nodeType === 3) return doText(root);
    if (root.nodeType !== 1) return;
    doEl(root);
    const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let n;
    while ((n = w.nextNode())) (n.nodeType === 3 ? doText(n) : doEl(n));
  };
  walk(document.body);
  const obs = new MutationObserver((muts) => {
    muts.forEach((m) => {
      if (m.type === "characterData") doText(m.target);
      else if (m.type === "attributes") doEl(m.target);
      else m.addedNodes.forEach(walk);
    });
  });
  obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  return () => obs.disconnect();
};

const Ctx = createContext({ lang: "en", t: (s) => s });

export const LangProvider = ({ children }) => {
  const lang = getLang();
  const [extra, setExtra] = useState(null);
  useEffect(() => {
    if (lang === "en") return;
    const review = isReview();
    Promise.all([
      http.get("/translations/overrides", { params: { lang } }).then((r) => r.data.ui).catch(() => ({})),
      review ? http.get("/translations/catalog", { params: { lang } }).then((r) => r.data.content).catch(() => []) : Promise.resolve([]),
    ]).then(([ov, cat]) => {
      cat.forEach(([path, src, tr]) => CATALOG.set(norm(tr), { key: path, source: src }));
      setExtra(ov);
    });
  }, [lang]);
  const lookup = useMemo(() => (lang === "en" || !extra ? () => null : build({ ...DICTS[lang], ...extra })), [lang, extra]);
  const value = useMemo(() => ({ lang, t: (s) => lookup(s) || s }), [lang, lookup]);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    if (lang === "en" || !extra) return undefined;
    return startDomTranslation(lookup);
  }, [lang, lookup, extra]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useLang = () => useContext(Ctx);

export const switchLang = (l) => {
  localStorage.setItem(LANG_KEY, l);
  window.location.reload();
};
